import { Box, Card, CardContent, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { generateSignedUrl } from "../../utils/getPreSignedURL";
import useSanitizeContent from "../../utils/useSanitizeContent";
import useThemeColor from "../../utils/useTheme";
import "./AdminPortal.css";
import { useDispatch } from "react-redux";
import { editUser, getCurrentUser } from "../../features/auth/authSlice";
import ReactPlayer from "react-player/lazy"; // Using lazy-loaded version for better performance

const isValidUrl = (url) => {
  return /^(https?:\/\/|www\.)/.test(url);
};

// Function to extract YouTube video ID from various YouTube URL formats
const getYoutubeVideoId = (url) => {
  if (!url) return null;

  // Handle different YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return match && match[2].length === 11 ? match[2] : null;
};

// Enhanced Video component that handles multiple video sources
const VideoPlayer = ({ videoLink, styles }) => {
  const [hasError, setHasError] = useState(false);

  if (!videoLink || hasError) return null;

  return (
    <Card
      sx={{
        margin: "0 auto",
        width: "50%",
        mb: 4,
        boxShadow: 4,
        borderRadius: { xs: 1, sm: 2 },
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          position: "relative",
          paddingTop: "56.25%" /* 16:9 Aspect Ratio */,
          backgroundColor: "#000"
        }}
      >
        <ReactPlayer
          url={videoLink}
          width="100%"
          height="100%"
          onError={() => setHasError(true)}
          controls={true}
          playing={false}
          light={false}
          pip={true}
          stopOnUnmount={true}
          style={{
            position: "absolute",
            top: 0,
            left: 0
          }}
          config={{
            file: {
              attributes: {
                controlsList: "nodownload",
                onContextMenu: (e) => e.preventDefault()
              },
              forceVideo: true
            },
            youtube: {
              playerVars: {
                modestbranding: 1,
                rel: 0
              }
            },
            vimeo: {
              playerOptions: {
                byline: false,
                portrait: false,
                title: false
              }
            }
          }}
        />
      </Box>
    </Card>
  );
};

const AdminPortal = ({
  value: htmlValue,
  userId,
  videoLink,
  checkNotesReminder
}) => {
  const [value, setValue] = useState("");
  const styles = useThemeColor();
  const dispatch = useDispatch();

  useEffect(() => {
    if (checkNotesReminder) {
      dispatch(editUser({ formData: { checkNotesReminder: true }, userId }));
      dispatch(getCurrentUser());
    }
  }, [userId, dispatch, checkNotesReminder]);

  const updateImageSources = useCallback(
    async (html, color) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));

      for (let img of images) {
        const fileName = img.getAttribute("src"); // Assuming the src contains the filename

        if (fileName && !fileName.startsWith("http")) {
          try {
            const signedUrl = await generateSignedUrl(
              fileName,
              process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
            );

            // Replace filename with the signed URL in the img tag
            img.src = signedUrl;
          } catch (error) {
            console.error("Error generating signed URL:", error);
          }
        }
        if (!img.hasAttribute("width")) {
          img.setAttribute("width", "100%");
        }
        if (!img.hasAttribute("height")) {
          img.setAttribute("height", "100%");
        }
      }

      const links = Array.from(doc.querySelectorAll("a"));
      links.forEach((link) => {
        link.target = "_blank";
        let href = link.getAttribute("href");
        link.style.background = ""; // Clear background
        link.style.color = ""; //
        if (href) {
          if (href.startsWith("www.")) {
            link.setAttribute("href", `https://${href}`);
          } else if (!isValidUrl(href)) {
            // Optionally prepend a default protocol (http or https)
            link.setAttribute("href", `https://${href}`);
          }
        }
      });
      const paragraphs = [
        ...Array.from(doc.querySelectorAll("span")),
        ...Array.from(doc.querySelectorAll("strong")),
        ...Array.from(
          doc.querySelectorAll(["h1", "h2", "h3", "h4", "h5", "h6"])
        )
      ];
      paragraphs.forEach((p) => {
        p.style.background = ""; // Clear background
        p.style.color = color; // Clear color
      });

      const allParas = Array.from(doc.querySelectorAll("p"));
      let consecutiveJustifyBrs = []; // Array to track sequences of <p> elements

      allParas.forEach((p, index) => {
        // Check if it's a target <p> element
        if (
          p.classList.contains("ql-align-justify") &&
          p.innerHTML.trim() === "<br>"
        ) {
          consecutiveJustifyBrs.push(p);
        } else {
          // If the sequence ends, check its length and remove if needed
          if (consecutiveJustifyBrs.length > 7) {
            const lastFourElements = consecutiveJustifyBrs.slice(-4);
            lastFourElements.forEach((element) => element.remove());
          }
          // Reset the sequence tracking array
          consecutiveJustifyBrs = [];
        }
        p.style.color = color;
      });

      // Handle any remaining sequence at the end of the loop
      if (consecutiveJustifyBrs.length > 7) {
        const lastFourElements = consecutiveJustifyBrs.slice(-4);
        lastFourElements.forEach((element) => element.remove());
      }
      // Set the updated HTML back to the editor
      setValue(doc.body.innerHTML);
    },
    [setValue]
  );

  useEffect(() => {
    const initialHtml = htmlValue || "<p>No content available.</p>";
    updateImageSources(initialHtml, styles.color);
  }, [updateImageSources, htmlValue, styles.color]);

  const sanitizedContent = useSanitizeContent(value);

  return (
    <Box
      sx={{
        padding: { xs: 0, sm: "0 16px", md: "0 25px" },
        width: "100%",
        margin: "0 auto"
      }}
      display={"flex"}
      gap="16px"
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      {/* Video Component at the top with full width */}
      {videoLink && <VideoPlayer videoLink={videoLink} styles={styles} />}

      {/* Content Section */}
      {sanitizedContent && sanitizedContent !== "undefined" ? (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: { xs: 1, sm: 2 },
            p: { xs: 1, sm: 2 },
            boxShadow: 2
          }}
        >
          <div
            className="preview-html"
            style={{
              marginTop: "10px",
              ".ql-align-center": {
                display: "flex",
                justifyContent: "center"
              },
              p: {
                color: `${styles.color} !important`
              },
              h1: {
                color: `${styles.color} !important`
              }
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizedContent
            }}
          />
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default AdminPortal;
